import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Heart, Target, Sparkles, ArrowRight, TrendingUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Testimonials from '../components/Testimonials';
import { fadeInUp, staggerContainer, scaleIn } from '../utils/animations';

// SVG Icons for problem section
const ProblemIcons = {
  overwhelming: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
    </svg>
  ),
  oneSize: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  ),
  wastedTime: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  science: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
    </svg>
  ),
  honest: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  ),
  community: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  ),
};

// Checkmark icon component
const CheckIcon = () => (
  <svg className="w-4 h-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const About = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>About MenoMatch | Our Mission to Empower Women Through Menopause</title>
        <meta name="description" content="Learn how MenoMatch helps women navigate menopause with personalized, science-backed supplement recommendations. Join 10,000+ women finding relief." />
        <meta name="keywords" content="about menomatch, menopause support, personalized menopause care, women's wellness" />

        {/* Open Graph */}
        <meta property="og:title" content="About MenoMatch | Empowering Women Through Menopause" />
        <meta property="og:description" content="Join 10,000+ women finding personalized menopause relief with MenoMatch." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <Header />

        {/* Hero Section - Improved */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-4xl mx-auto px-4 py-16 md:py-20"
        >
          <div className="text-center">
            <motion.div
              variants={scaleIn}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-100 to-secondary-100 text-primary-700 px-4 py-2 rounded-full font-semibold text-sm mb-6"
            >
              <Sparkles className="w-4 h-4" />
              <span>Our Story</span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              Empowering Women Through{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">
                Menopause
              </span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto"
            >
              MenoMatch was born from a simple belief: every woman deserves
              personalized support during menopause. No two journeys are alike,
              and your solutions shouldn't be either.
            </motion.p>
          </div>
        </motion.section>

        {/* The Problem Section */}
        <section className="py-16 md:py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-12 items-center"
            >
              <motion.div variants={fadeInUp}>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                  The Problem We're Solving
                </h2>
                <div className="space-y-6">
                  <motion.div
                    variants={fadeInUp}
                    className="flex gap-4"
                  >
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0 text-red-600">
                      {ProblemIcons.overwhelming}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2 text-gray-900">Overwhelming Information</h3>
                      <p className="text-gray-600 leading-relaxed">
                        Thousands of supplements, conflicting advice, and no clear path forward.
                        It's exhausting to figure out what actually works.
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    variants={fadeInUp}
                    className="flex gap-4"
                  >
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0 text-orange-600">
                      {ProblemIcons.oneSize}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2 text-gray-900">One-Size-Fits-All Fails</h3>
                      <p className="text-gray-600 leading-relaxed">
                        Generic recommendations ignore your unique symptoms, lifestyle, and body.
                        What works for your friend might not work for you.
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    variants={fadeInUp}
                    className="flex gap-4"
                  >
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0 text-amber-600">
                      {ProblemIcons.wastedTime}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2 text-gray-900">Wasted Time & Money</h3>
                      <p className="text-gray-600 leading-relaxed">
                        Trial and error with products that don't address YOUR specific needs.
                        You deserve better than guesswork.
                      </p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              <motion.div
                variants={scaleIn}
                className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mb-6">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Our Solution</h3>
                <p className="text-gray-700 leading-relaxed mb-6 text-lg">
                  A personalized, science-backed quiz that analyzes YOUR symptoms
                  and recommends supplements proven to work for women like you.
                </p>
                <div className="bg-gradient-to-r from-primary-50 to-secondary-50 p-6 rounded-xl">
                  <div className="flex items-center justify-center gap-2 text-primary-700 font-bold text-lg">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                    </svg>
                    <span>Free • 2 Minutes • Personalized</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
            >
              <motion.h2
                variants={fadeInUp}
                className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4"
              >
                What We Stand For
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="text-xl text-gray-600 text-center mb-12 max-w-2xl mx-auto"
              >
                These core values guide everything we do at MenoMatch
              </motion.p>

              <motion.div
                variants={staggerContainer}
                className="grid md:grid-cols-3 gap-8 md:gap-10"
              >
                <motion.div
                  variants={fadeInUp}
                  whileHover={{ y: -8 }}
                  className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-6 text-blue-600">
                    {ProblemIcons.science}
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-gray-900">Science-Backed</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Every recommendation is based on peer-reviewed research and clinical studies.
                    No pseudoscience, just proven solutions.
                  </p>
                </motion.div>

                <motion.div
                  variants={fadeInUp}
                  whileHover={{ y: -8 }}
                  className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mx-auto mb-6 text-green-600">
                    {ProblemIcons.honest}
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-gray-900">Honest & Transparent</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We're upfront about affiliate partnerships and never recommend products
                    we don't believe in. Your trust matters more than commissions.
                  </p>
                </motion.div>

                <motion.div
                  variants={fadeInUp}
                  whileHover={{ y: -8 }}
                  className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto mb-6 text-purple-600">
                    {ProblemIcons.community}
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-gray-900">Community First</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Your wellbeing matters more than any sale. We're here to support you,
                    not just sell to you.
                  </p>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Stats/Impact Section */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
          className="py-16 md:py-20 bg-gradient-to-r from-primary-600 via-primary-500 to-secondary-500 text-white relative overflow-hidden"
        >
          {/* Decorative elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-6xl mx-auto px-4 relative z-10">
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold text-center mb-4"
            >
              Our Impact So Far
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-primary-100 text-center mb-12 max-w-2xl mx-auto"
            >
              Helping thousands of women find relief, one personalized recommendation at a time
            </motion.p>

            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
            >
              <motion.div
                variants={scaleIn}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl font-bold mb-2">10,000+</div>
                <div className="text-primary-100 text-lg">Quizzes Taken</div>
              </motion.div>

              <motion.div
                variants={scaleIn}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl font-bold mb-2">4.8★</div>
                <div className="text-primary-100 text-lg">Average Rating</div>
              </motion.div>

              <motion.div
                variants={scaleIn}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl font-bold mb-2">94%</div>
                <div className="text-primary-100 text-lg">Satisfaction Rate</div>
              </motion.div>

              <motion.div
                variants={scaleIn}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl font-bold mb-2">8</div>
                <div className="text-primary-100 text-lg">Educational Guides</div>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* How It Works */}
        <section className="py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
            >
              <motion.h2
                variants={fadeInUp}
                className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12"
              >
                How MenoMatch Works
              </motion.h2>

              <motion.div
                variants={staggerContainer}
                className="grid md:grid-cols-3 gap-8"
              >
                <motion.div
                  variants={fadeInUp}
                  className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-all relative"
                >
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4 mt-2">
                    <Target className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Take Our Quiz</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Answer 10 simple questions about your symptoms, lifestyle, and wellness preferences.
                  </p>
                </motion.div>

                <motion.div
                  variants={fadeInUp}
                  className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-all relative"
                >
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-secondary-600 text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div className="w-16 h-16 bg-secondary-100 rounded-2xl flex items-center justify-center mx-auto mb-4 mt-2">
                    <Sparkles className="w-8 h-8 text-secondary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Get Personalized Matches</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our algorithm analyzes your responses to find supplements proven to work for your specific needs.
                  </p>
                </motion.div>

                <motion.div
                  variants={fadeInUp}
                  className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-all relative"
                >
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4 mt-2">
                    <TrendingUp className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Start Your Journey</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Explore your recommendations, read detailed guides, and begin feeling like yourself again.
                  </p>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Section */}
        <Testimonials />

        {/* Final CTA Section */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="max-w-4xl mx-auto px-4 py-16 md:py-20"
        >
          <motion.div
            variants={fadeInUp}
            className="text-center bg-white rounded-2xl shadow-2xl p-10 md:p-16 border border-gray-100"
          >
            <motion.div
              variants={scaleIn}
              className="w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mx-auto mb-6"
            >
              <Heart className="w-10 h-10 text-white" />
            </motion.div>

            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
            >
              Ready to Find Your Match?
            </motion.h2>

            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              Join 10,000+ women who've found personalized menopause relief.
              Take our free 2-minute quiz and start feeling like yourself again.
            </motion.p>

            <motion.div
              variants={fadeInUp}
            >
              <Link
                to="/"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-10 py-5 rounded-full font-bold text-xl hover:shadow-2xl transition-all transform hover:scale-105"
              >
                Start Your Free Quiz
                <ArrowRight className="w-6 h-6" />
              </Link>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-4 text-sm text-gray-500 mt-6"
            >
              <span className="flex items-center gap-1">
                <CheckIcon />
                100% Free
              </span>
              <span className="flex items-center gap-1">
                <CheckIcon />
                2 Minutes
              </span>
              <span className="flex items-center gap-1">
                <CheckIcon />
                Science-Backed
              </span>
              <span className="flex items-center gap-1">
                <CheckIcon />
                Personalized Results
              </span>
            </motion.div>
          </motion.div>
        </motion.section>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default About;
